import { Component, ReactNode } from 'react'
import * as cn from 'classnames'
import * as moment from 'moment'
import * as styles from 'styles/molecules/tables/CommonHeaderMobile.css'
import { TableContextType, TableContext } from 'components/molecules/tables/Table2'
import { SortingSelector } from 'components/molecules/tables/Table'
import { AccountType, DetailedAccount } from 'interfaces/accounts'
import { CreditAccount } from 'interfaces/credit'
import { createAccountSortingSelector } from 'helpers/accountHelpers'
import { CreditscorePageType } from 'interfaces/pages'
import { FullMoneyflowType } from 'interfaces/moneyflow'
import withContext from 'hocs/withContext/withContext'

type ColumnItem<TEntity = {}> = { name: string, title: string, sorting?: SortingSelector<TEntity> | boolean }

const amountSelector: SortingSelector<DetailedAccount> =
  (row) => row.link_status === 'unlinked' ? undefined : row.amount
const paymentDateSelector: SortingSelector<DetailedAccount> =
  createAccountSortingSelector(({ due_date }) => due_date !== null ? moment(due_date).valueOf() : null)

const MONEYFLOW_COLUMNS_MAP: ColumnItem[] = [
  { name: 'date', title: 'Date', sorting: true },
  { name: 'description', title: 'Description', sorting: true },
  { name: 'amount', title: 'Amount', sorting: true },
  { name: 'account__name', title: 'Account', sorting: true },
]

const ACCOUNT_TYPE_COLUMN: ColumnItem<DetailedAccount | CreditAccount> = {
  name: 'account_type',
  title: 'Type',
  sorting: (row) => row.category.value,
}

const ACCOUNT_NAME_COLUMN: ColumnItem<DetailedAccount | CreditAccount> = {
  name: 'account_name',
  title: 'Name',
  sorting: (row) => row.name,
}

// TODO unify with AccountsDetailsHeader
const DEBTS_COLUMNS_MAP: ColumnItem<DetailedAccount>[] = [
  ACCOUNT_TYPE_COLUMN,
  ACCOUNT_NAME_COLUMN,
  { name: 'rate', title: 'Rate', sorting: createAccountSortingSelector((row) => row.rate) },
  { name: 'amount', title: 'Balance', sorting: amountSelector },
  { name: 'payment_date', title: 'Payment date', sorting: paymentDateSelector },
]

const ASSETS_COLUMNS_MAP: ColumnItem<DetailedAccount>[] = [
  ACCOUNT_TYPE_COLUMN,
  ACCOUNT_NAME_COLUMN,
  { name: 'amount', title: 'Balance', sorting: amountSelector },
]

const CREDIT_COLUMNS_MAP: ColumnItem<CreditAccount>[] = [
  ACCOUNT_TYPE_COLUMN,
  ACCOUNT_NAME_COLUMN,
  { name: 'condition', title: 'Condition', sorting: (row) => row.condition },
  { name: 'balance', title: 'Balance', sorting: (row) => row.balance },
  { name: 'opened_at', title: 'Date opened', sorting: (row) => moment(row.opened_at).valueOf() },
]

const REDEMPTIONS_COLUMNS_MAP: ColumnItem[] = [
  { name: 'date', title: 'Date', sorting: true },
  { name: 'account', title: 'Account' },
  { name: 'amount', title: 'Amount' },
]

type CommonHeaderMobileProps = {
  table: TableContextType
  type:
    | FullMoneyflowType
    | AccountType
    | CreditscorePageType
    | 'redemptions'
}

// TODO unify with Table2.HeadCell
class CommonHeaderMobile extends Component<CommonHeaderMobileProps> {
  private get columnsMap () {
    const { type } = this.props

    switch (type) {
      case 'spending':
      case 'income':
      case 'transactions':
        return MONEYFLOW_COLUMNS_MAP

      case 'creditscore':
        return CREDIT_COLUMNS_MAP

      case 'debts':
        return DEBTS_COLUMNS_MAP

      case 'assets':
        return ASSETS_COLUMNS_MAP

      case 'redemptions':
        return REDEMPTIONS_COLUMNS_MAP

      default:
        return []
    }
  }

  render () {
    const { type } = this.props
    const columns = this.columnsMap.map(this.renderColumnName)

    const className = cn(styles.commonHeaderWrapper, {
      [styles.moneyflow]: type === 'spending' || type === 'income',
      [styles.debts]: type === 'debts',
      [styles.assets]: type === 'assets',
      [styles.credit]: type === 'creditscore',
      [styles.redemptions]: type === 'redemptions',
    })

    return (
      <tr>
        <th>
          <div className={ className }>
            <div className={ styles.columnsPrefix }>
              Sort By:
            </div>
            <div className={ styles.columnNamesWrapper }>
              { columns }
            </div>
          </div>
        </th>
      </tr>
    )
  }

  renderColumnName = ({ name, title }: ColumnItem) => {
    const { sortableColumns } = this.props.table
    const sortableColumn = sortableColumns[name]
    const isColumnSortable = Boolean(sortableColumn)
    const handleClick = isColumnSortable
      ? () => this.handleColumnClick(name)
      : undefined

    return (
      <div
        key={ name }
        className={ styles.columnNameWrapper }
        onClick={ handleClick }
      >
        <div className={ styles.columnName }>
          { title }
        </div>
        { this.getSortingElement(name) }
      </div>
    )
  }

  componentDidMount () {
    this.columnsMap.forEach(({ name, sorting }) => {
      if (!sorting) {
        return
      }
      this.props.table.addSortableColumn(name, typeof sorting === 'function' ? sorting : undefined)
    })
  }

  private getSortingElementClasses = (name: string): {} | null => {
    const { sorting } = this.props.table
    if (!sorting || sorting.columnName !== name) {
      return null
    }

    return {
      [styles.up]: sorting.direction === 'asc',
      [styles.down]: sorting.direction === 'desc',
    }
  }

  private getSortingElement = (name: string): ReactNode => {
    const { sortableColumns, sorting } = this.props.table
    const sortableColumn = sortableColumns[name]

    if (!sortableColumn || !sorting || sorting.columnName !== name) {
      return null
    }

    return (
      <div className={ cn(styles.sortingWrapper, this.getSortingElementClasses(name)) } />
    )
  }

  private handleColumnClick = (name: string) => {
    const { sorting } = this.props.table

    const direction = sorting && sorting.columnName === name
      ? (sorting.direction === 'desc' ? 'asc' : 'desc')
      : 'asc'

    this.props.table.onChangeSorting({ columnName: name, direction })
  }
}

export default withContext(TableContext, 'table')(CommonHeaderMobile)
